import { PermissionModeEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/permission-gps.enum';
import { t, tEnum } from 'app/core/i18n/i18n';
import { TranslationKey } from 'app/core/i18n/translation-keys';

export const USE_GPS_MODE_LABEL_KEYS: Record<PermissionModeEnum, TranslationKey> = {
  [PermissionModeEnum.PERMISSIVE]: 'routing.SUB_ROUTE_PARTNERS.PERMISSION_GPS.MODE_LABEL_PERMISSIVE',
  [PermissionModeEnum.RESTRICTIVE]: 'routing.SUB_ROUTE_PARTNERS.PERMISSION_GPS.MODE_LABEL_RESTRICTIVE',
  [PermissionModeEnum.ROUTE_BASED]: 'routing.SUB_ROUTE_PARTNERS.PERMISSION_GPS.MODE_LABEL_ROUTE_BASED',
  [PermissionModeEnum.COMPANY_BASED]: 'routing.SUB_ROUTE_PARTNERS.PERMISSION_GPS.MODE_LABEL_COMPANY_BASED',
};

/**
 * Existing global flat keys (`src/assets/i18n/es.json` / `en.json`, root scope) already used
 * elsewhere in the app for this exact enum. Reused here for the mat-select in the bulk GPS dialog
 * — do NOT introduce new keys with different text for the same select, that would be a silent copy
 * change (the select currently reads "PERMISIVO" / "RESTRICTIVO" / "DE ACUERDO A RUTA", not
 * "Permisivo" / "Restrictivo" / "De acuerdo a ruta").
 */
export const USE_GPS_MODE_SELECT_LABEL_KEYS: Record<PermissionModeEnum, TranslationKey> = {
  [PermissionModeEnum.PERMISSIVE]: 'PERMISSIVE',
  [PermissionModeEnum.RESTRICTIVE]: 'RESTRICTIVE',
  [PermissionModeEnum.ROUTE_BASED]: 'ROUTE_BASED',
  [PermissionModeEnum.COMPANY_BASED]: 'COMPANY_BASED',
};

export interface UseGpsModeOption {
  key: string;
  value: PermissionModeEnum;
}

export const USE_GPS_MODE_OPTIONS: UseGpsModeOption[] = [
  { key: 'PERMISSIVE', value: PermissionModeEnum.PERMISSIVE },
  { key: 'RESTRICTIVE', value: PermissionModeEnum.RESTRICTIVE },
  { key: 'ROUTE_BASED', value: PermissionModeEnum.ROUTE_BASED },
];

/** Label for the mat-select in the bulk GPS dialog (uses the pre-existing global keys). */
export function useGpsModeSelectLabel(value: PermissionModeEnum): string {
  return tEnum(USE_GPS_MODE_SELECT_LABEL_KEYS, value) ?? '-';
}

export function formatUseGpsMode(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '-';
  }
  return tEnum(USE_GPS_MODE_LABEL_KEYS, value as PermissionModeEnum) ?? '-';
}

export const USE_GPS_MODE_CHIP_CLASSES: Record<PermissionModeEnum, string> = {
  [PermissionModeEnum.PERMISSIVE]:
    'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  [PermissionModeEnum.RESTRICTIVE]:
    'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  [PermissionModeEnum.ROUTE_BASED]:
    'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  [PermissionModeEnum.COMPANY_BASED]:
    'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
};

export function getUseGpsModeChipClasses(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  }
  return (
    USE_GPS_MODE_CHIP_CLASSES[value as PermissionModeEnum] ??
    'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
  );
}

const GPS_CHIP_BASE_CLASSES =
  'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium';

function gpsChipHtml(label: string, colorClasses: string): string {
  return `<span class="${GPS_CHIP_BASE_CLASSES} ${colorClasses}">${label}</span>`;
}

export function formatUseGpsValidationChipHtml(useGps: boolean | null | undefined): string {
  const active = !!useGps;
  return gpsChipHtml(
    active
      ? t('routing.SUB_ROUTE_PARTNERS.PERMISSION_GPS.CHIP_ACTIVE')
      : t('routing.SUB_ROUTE_PARTNERS.PERMISSION_GPS.CHIP_INACTIVE'),
    active
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
  );
}

export function formatUseGpsModeChipHtml(mode: number | null | undefined): string {
  const label = formatUseGpsMode(mode);
  if (label === '-') {
    return '<span class="text-xs text-gray-500">-</span>';
  }
  return gpsChipHtml(label, getUseGpsModeChipClasses(mode));
}
